
import { mapActions, mapState } from 'vuex'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import freeEmailProviders from 'free-email-domains'
import afterLoginActions from '@/components/tools/afterLoginActions'
import api from '@/components/tools/api'

export default {
  components: {
    ValidationObserver,
    ValidationProvider
  },
  props: {
    popup: {
      type: Boolean,
      default: false
    },
    redirect: {
      type: String,
      default: null
    },
    overrideTitle: {
      type: String,
      default: null
    },
    overrideSubtitle: {
      type: String,
      default: null
    },
    overrideEmail: {
      type: String,
      default: null
    },
    callback: {
      type: Function,
      default: null
    }
  },
  data () {
    return {
      step: 1,
      form: {
        email: '',
        password: ''
      },
      isSubmitting: false
    }
  },
  fetch () {
    if (this.isValidEmail(this.overrideEmail)) {
      this.form.email = this.overrideEmail
    }
  },

  computed: {
    ...mapState('customer', {
      customerId: state => state.customer.customerId,
      profileType: state => state.profileType,
      hasCompletedProfileData: state => state.hasCompletedProfileData
    }),
    ...mapState('upload', {
      product: state => state.product
    }),

    title () {
      if (this.overrideTitle) {
        return this.overrideTitle
      }
      switch (this.step) {
        case 1:
          return this.$t('login_or_register')
        case 2:
          return this.$t('login')
        default:
          return this.$t('register')
      }
    },
    subtitle () {
      if (this.overrideSubtitle) {
        return this.overrideSubtitle
      }
      return this.$t('ad.register.fast')
    },
    buttonText () {
      switch (this.step) {
        case 1:
          return this.$t('continue')
        case 2:
          return this.$t('login')
        default:
          return this.$t('register_no_cost')
      }
    },
    emailLabel () {
      return this.$t('email')
    },
    emailHint () {
      const { email } = this.form
      const provider = email.split('@')[1]
      const providerDomain = provider?.split('.')[1]

      if (
        !providerDomain ||
        providerDomain.length < 2 ||
        freeEmailProviders.includes(provider)
      ) {
        return
      }

      return this.$t('validation.email_provider', { _value_: email })
    },
    passwordLabel () {
      return this.$t('auth.password')
    }
  },

  methods: {
    ...mapActions('upload', ['setOverlay', 'getProduct', 'updateProduct']),
    ...mapActions('general', ['showSnackbar']),
    ...mapActions('customer', ['getCustomer']),
    isValidEmail (email) {
      if (!email) {
        return false
      }
      if (typeof email !== 'string') {
        return false
      }
      // Simple RFC 5322 compliant regex for email validation
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    },
    async tryUpdateProductAfterLogin () {
      if (this.product.entityId) {
        await this.updateProduct({ entityId: this.product.entityId })
      }
    },

    async redirectToCatalogIfNotOwner () {
      if (this.$auth.loggedIn && this.$route.name.startsWith('ad-edit') && this.product.entityId) {
        await this.getCustomer()
        try {
          await this.getProduct(this.product.entityId)
          if (this.customerId !== this.product.customerId && this.$route.name.startsWith('ad-edit')) {
            await this.$router.push('/')
          }
        } catch (e) {
          await this.$router.push('/')
        }
      }
    },

    async resetPassword () {
      await this.$axios.post(
        '/public/customer/auth/password/change',
        { email: this.form.email },
        { withCredentials: true }
      )
      await this.showSnackbar({
        text: this.$t('password_reset_email_sent'),
        color: 'success'
      })
    },
    getCsrfToken () {
      return api.getCsrfToken(this)
    },
    sanitizeEmail () {
      if (typeof this.form.email === 'string' && this.form.email) {
        this.form.email = this.form.email.trim().toLowerCase()
        return true
      } else {
        this.showSnackbar({
          text: this.$t('auth.invalid_email'),
          color: 'error'
        })
        this.form.email = ''
        return false
      }
    },

    findCustomer (email) {
      return this.getCsrfToken().then(response =>
        this.$axios.$post(
          '/public/customer/auth/user',
          { email },
          {
            withCredentials: true,
            headers: {
              'X-Csrf-Token': response.csrfToken,
              'X-Session-Id': response.sessionId
            }
          }
        )
      )
    },

    async submit () {
      // const unsanitized = this.form.email
      const valid = this.sanitizeEmail()
      const email = this.form.email
      const password = this.form.password
      if (!valid) {
        return
      }

      if (this.step > 1) {
        await this.login(email, password)

        return
      }

      this.isSubmitting = true

      try {
        const userExists = await this.findCustomer(email)
        this.step = userExists ? 2 : 3
      } catch (e) {
        const errorMessageKey = e?.response?.data?.error?.includes('too_many_attempts')
          ? 'auth.too_many_attempts'
          : 'auth.error_reloading'
        await this.showSnackbar({
          text: this.$t(errorMessageKey),
          color: 'error'
        })
        //
        // setTimeout(() => {
        //   window.location.reload()
        // }, 3000)
      } finally {
        this.isSubmitting = false
      }
    },
    async login (email, password) {
      this.isSubmitting = true

      try {
        await this.getCsrfToken().then(response =>
          this.$auth.loginWith('local', {
            data: {
              email,
              password
            },

            headers: {
              'X-Csrf-Token': response.csrfToken,
              'X-Session-Id': response.sessionId
            }

          })
        )

        await this.$axios.post(
          '/private/product/take-ownership',
          {},
          { withCredentials: true }
        )

        try {
          await this.getCustomer()
        } catch (e) {
          console.error('Error fetching customer data:', e)
        }

        try {
          console.log('Identifying user with Chatbase')
          await this.$chatbaseIdentify()
        } catch (e) {
          console.error('Failed to identify user with Chatbase', e)
        }
        await this.setOverlay(false)

        if (this.callback) {
          await this.callback()
          return
        }

        if (this.redirect) {
          if (this.profileType === 'MERCHANT' && this.hasCompletedProfileData) {
            await this.$router.push(this.localePath({
              name: 'catalog',
              query: {
                auction_status: 'running'
              }
            }))
            return
          } else if (this.hasCompletedProfileData) {
            await this.$router.push(this.localePath({
              name: 'account-my-vehicles'
            }))
            return
          }

          await this.$router.push(this.localePath({ name: this.redirect }))
        } else {
          await this.$router.push({ path: this.$route.path })
        }

        try {
          await this.tryUpdateProductAfterLogin()
        } catch (e) {
          console.error(e)
        }

        try {
          afterLoginActions.gtmLogNewRegistration(this, this.customerId)
          afterLoginActions.tapfiliateLogNewRegistration(this, this.customerId)
        } catch (e) {
          console.error(e)
        }

        try {
          const url = new URL(location.href)
          url.searchParams.delete('refresh')
          history.replaceState(null, '', url)
        } catch (e) {
          console.error(e)
        }
      } catch (e) {
        const isLoginError =
          e?.response?.data?.error?.includes('invalid_grant') || e?.response?.data?.error?.includes('invalid_request')

        if (isLoginError) {
          this.$refs.validationObserver.setErrors({
            password: [this.$t('auth.invalid_user_and_password')],
            password_register: [this.$t('auth.invalid_password')]
          })

          return
        }

        const errorMessageKey = e?.response?.data?.error?.includes('too_many_attempts')
          ? 'auth.too_many_attempts'
          : 'error'
        await this.showSnackbar({
          text: this.$t(errorMessageKey),
          color: 'error'
        })

        // if (!e?.includes('too_many_attempts')) {
        //   setTimeout(() => {
        //     window.location.reload()
        //   }, 3000)
        // }
      } finally {
        this.isSubmitting = false
      }

      await this.redirectToCatalogIfNotOwner()
    }
  }
}
